import {
  EPG_MAX_SOURCES,
  EpgSource,
  EpgSourceStatus,
  EpgStatus,
} from '@/features/master/masterSlice';

// ---------------------------------------------------------------------------
// Pure logic behind the EPG sources page. Kept out of Epg.tsx so the rules that
// actually cost debugging time — the URL check, the duplicate scan, the
// index-based status join, the pending-vs-live comparison — can be exercised
// without a React tree or the store.
// ---------------------------------------------------------------------------

// Deliberately permissive, and deliberately NOT the old page's regex. That one
// demanded a dotted hostname plus a trailing path segment, so an on-prem
// operator serving the guide from `http://localhost/epg`, a bare LAN IP, a
// host:port or an apex domain literally could not save. The server validates
// scheme / host / duplicates and is authoritative.
export const URL_RE = /^https?:\/\/.+/i;

// The edit buffer for one row.
//
// `id` is a SYNTHETIC stable key. The list is reorderable, and with array-index
// keys React pins DOM nodes to screen positions: tab to Source 3's Up button,
// press Enter, the row moves — but focus stays on the button now owned by the
// row that was just demoted, so a second Enter swaps them straight back. `url`
// is no good as a key either: it is blank while being typed, and duplicates are
// explicitly possible.
//
// The `saved*` fields are the last values the SERVER confirmed. `savedIndex` is
// how the status row is looked up, and the comparisons are what tell a pending
// edit apart from a live one, so the page can say "not saved yet" instead of
// implying the edit is already serving.
export interface SourceEdit {
  id:				number;
  name:				string;
  url:				string;
  disabled:			boolean;
  savedName:		string;
  savedUrl:			string;
  savedDisabled:	boolean;
  savedIndex:		number;	// -1 for a row that has never been saved
}

let nextSourceId = 1;

export function toSourceEdit(src: EpgSource, index: number): SourceEdit {
  // A row can come back as a bare {"url": "..."} — `name` and `disabled` are
  // omitempty on the wire. Normalise on read.
  const name = src.name || '';
  const url = src.url || '';
  const disabled = !!src.disabled;

  return {
    id:				nextSourceId++,
    name:			name,
    url:			url,
    disabled:		disabled,
    savedName:		name,
    savedUrl:		url,
    savedDisabled:	disabled,
    savedIndex:		index,
  };
}

export function toSourceEdits(sources?: EpgSource[]): SourceEdit[] {
  return (sources || []).map(toSourceEdit);
}

export function newSourceEdit(): SourceEdit {
  return {
    id:				nextSourceId++,
    name:			'',
    url:			'',
    disabled:		false,
    savedName:		'',
    savedUrl:		'',
    savedDisabled:	false,
    savedIndex:		-1,
  };
}

export function sourceUrlInvalid(src: SourceEdit): boolean {
  const url = (src.url || '').trim();

  return url === '' || !URL_RE.test(url);
}

// Blank rows are skipped: they already fail the "missing URL" check, and two of
// them are not a meaningful duplicate report.
export function sourceDuplicate(src: SourceEdit, index: number, all: SourceEdit[]): boolean {
  const url = (src.url || '').trim();

  if (url === '')
    return false;

  return all.some((other, j) => j !== index && (other.url || '').trim() === url);
}

export type RowError = 'none' | 'url' | 'duplicate';

// "Missing URL" outranks "duplicate": a blank row is not a duplicate of
// anything, and reporting both on one field just hides the real problem.
export function rowError(src: SourceEdit, index: number, all: SourceEdit[]): RowError {
  if (sourceUrlInvalid(src))
    return 'url';

  return sourceDuplicate(src, index, all) ? 'duplicate' : 'none';
}

export function sourcesInvalid(all: SourceEdit[]): boolean {
  return all.some((s, i) => rowError(s, i, all) !== 'none');
}

// A row differs from what the server last confirmed. `savedIndex !== index`
// counts: reordering is a real edit — the top enabled feed owns a shared
// channel id.
export function rowPending(src: SourceEdit, index: number): boolean {
  return	src.savedIndex < 0 ||
  			src.savedIndex !== index ||
  			(src.url || '').trim() !== src.savedUrl ||
  			(src.name || '').trim() !== src.savedName ||
  			src.disabled !== src.savedDisabled;
}

export function listChanged(all: SourceEdit[], savedCount: number): boolean {
  return all.length !== savedCount || all.some(rowPending);
}

// Immutable reorder. Returns the SAME array when the move would run off either
// end, so a no-op cannot mark the form dirty.
export function moveSource(all: SourceEdit[], index: number, delta: number): SourceEdit[] {
  const to = index + delta;

  if (index < 0 || index >= all.length || to < 0 || to >= all.length)
    return all;

  const next = [...all];
  const [row] = next.splice(index, 1);
  next.splice(to, 0, row);

  return next;
}

export function canAddSource(all: SourceEdit[]): boolean {
  return all.length < EPG_MAX_SOURCES;
}

export function addSource(all: SourceEdit[]): SourceEdit[] {
  // Disable Add at the ceiling rather than letting the server reject the whole
  // save over one extra row.
  return canAddSource(all) ? [...all, newSourceEdit()] : all;
}

export function removeSource(all: SourceEdit[], index: number): SourceEdit[] {
  return all.filter((_, j) => j !== index);
}

// `epg_sources` present ⇒ the whole list is REPLACED, so this always emits every
// row the admin wants to keep, in the order they want it. Sending "" / false is
// harmless — the Go side is omitempty.
export function buildEpgSourcesPayload(all: SourceEdit[]): EpgSource[] {
  return all.map((s) => ({
    name:		(s.name || '').trim(),
    url:		(s.url || '').trim(),
    disabled:	s.disabled,
  }));
}

// BY INDEX, never by URL. The server redacts credentials, so a feed saved as
// https://user:pass@host/epg.xml comes back as https://xxx@host/epg.xml and a
// string join would silently match nothing — the row would render no health at
// all. `sources` is returned one per configured feed in saved order, so the
// index is exact.
export function statusForRow(status: EpgStatus | null, src: SourceEdit): EpgSourceStatus | null {
  if (!status || src.savedIndex < 0)
    return null;

  return status.sources[src.savedIndex] || null;
}

export type RowStatusKind =
  | 'unsaved'		// never saved — there is no server row to report on
  | 'unavailable'	// the status endpoint is not answering
  | 'unknown'		// no snapshot yet, or no row at this index
  | 'disabled'		// switched off server-side
  | 'failed'		// no usable data at all
  | 'stale'			// carrying last good data forward after a failure
  | 'ok';

export function rowStatusKind(
  src: SourceEdit,
  st: EpgSourceStatus | null,
  hasSnapshot: boolean,
  statusFailed: boolean,
): RowStatusKind {
  if (src.savedIndex < 0)
    return 'unsaved';

  if (!hasSnapshot && statusFailed)
    return 'unavailable';

  if (!st)
    return 'unknown';

  // The badge reads the SERVER's flag, not the edit buffer — otherwise
  // unticking Enabled instantly shows "Turned off" for a feed that is still
  // live and merging.
  if (src.savedDisabled)
    return 'disabled';

  if (!st.ok)
    return 'failed';

  // `ok` stays true through an outage — it only means "has usable data".
  // `stale` is the flag that says the data is being carried forward.
  return st.stale ? 'stale' : 'ok';
}

export const POLL_MS = 30000;

export interface StatusPollOptions {
  admin:			boolean;
  changes:			boolean;
  load:				() => any;
  isVisible:		() => boolean;
  intervalMs?:		number;
  timers?:			{
    set:			(fn: () => void, ms: number) => any;
    clear:			(handle: any) => void;
  };
}

// The body of the status poll effect. Factored out of the component so the two
// traps in it can be exercised directly.
//
// 1. The guard runs BEFORE the first call. `changes` is an effect dependency,
//    so false→true re-runs the whole body; calling load() first would fire a
//    request on the very first keystroke of an edit — precisely the moment the
//    guard exists to prevent.
// 2. Every poll refreshes the session's idle deadline, and no other settings
//    page polls at all, so a forgotten background tab would keep an admin
//    session alive indefinitely. Hence the visibility gate on each tick.
//
// Returns the effect's cleanup, or undefined when no timer was started.
export function startStatusPoll(opts: StatusPollOptions): (() => void) | undefined {
  if (!opts.admin || opts.changes)
    return undefined;

  opts.load();

  const timers = opts.timers || {
    set:	(fn: () => void, ms: number) => setInterval(fn, ms),
    clear:	(handle: any) => clearInterval(handle),
  };

  const handle = timers.set(() => {
    if (opts.isVisible())
      opts.load();
  }, opts.intervalMs === undefined ? POLL_MS : opts.intervalMs);

  return () => timers.clear(handle);
}

export function formatWhen(value?: string): string {
  if (!value)
    return '';

  const d = new Date(value);

  return isNaN(d.getTime()) ? value : d.toLocaleString();
}
